import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Buildings,
  Users,
  Briefcase,
  Warning,
  CheckCircle,
  XCircle,
  ClockCounterClockwise,
  CircleNotch,
} from '@phosphor-icons/react';
import { platformApi, getPlatformApiErrorMessage } from '@/lib/platformApi';

interface OverviewCounts {
  totalTenants: number;
  activeTenants: number;
  suspendedTenants: number;
  trialTenants: number;
  trialsEndingSoon: number;
  totalUsers: number;
  totalCases: number;
}

interface RecentSignup {
  id: string;
  name: string;
  slug: string;
  plan: string;
  status: string;
  createdAt: string;
}

interface OverviewData {
  counts: OverviewCounts;
  recentSignups: RecentSignup[];
}

function formatDate(str: string) {
  return new Date(str).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active: 'bg-plat-success-soft text-plat-success-text',
    trial: 'bg-plat-warning-soft text-plat-warning-text',
    suspended: 'bg-plat-danger-soft text-plat-danger-text',
  };
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize ${map[status] ?? 'bg-plat-neutral-soft text-plat-secondary'}`}>
      {status}
    </span>
  );
}

function PlanBadge({ plan }: { plan: string }) {
  const map: Record<string, string> = {
    trial: 'bg-plat-warning-soft text-plat-warning-text',
    starter: 'bg-plat-info-soft text-plat-info-text',
    pro: 'bg-plat-indigo-soft text-plat-indigo-text',
    enterprise: 'bg-plat-purple-soft text-plat-purple-text',
  };
  return (
    <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium capitalize ${map[plan] ?? 'bg-plat-neutral-soft text-plat-secondary'}`}>
      {plan}
    </span>
  );
}

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  accent?: string;
}

function StatCard({ label, value, icon, accent = 'text-plat-info-text' }: StatCardProps) {
  return (
    <div className="rounded-xl border border-plat-border bg-plat-surface p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.08em] text-plat-muted">{label}</div>
          <div className="mt-2 text-[28px] font-bold leading-none text-plat-fg">{value}</div>
        </div>
        <div className={`${accent} mt-1`}>{icon}</div>
      </div>
    </div>
  );
}

export default function PlatformDashboardPage() {
  const { data, isLoading, error } = useQuery<OverviewData>({
    queryKey: ['platform', 'overview'],
    queryFn: async () => {
      const res = await platformApi.get('/overview');
      return res.data?.data ?? res.data;
    },
    refetchInterval: 60_000,
  });

  const todayStr = new Date().toLocaleDateString('en-GB', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <CircleNotch className="h-7 w-7 animate-spin text-plat-info-text" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-xl border border-plat-danger-line bg-plat-danger-soft p-6 text-sm text-plat-danger-text">
        Failed to load overview: {getPlatformApiErrorMessage(error)}
      </div>
    );
  }

  if (!data) return null;

  const { counts, recentSignups } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-2xl font-bold text-plat-fg">Platform Overview</h2>
          <p className="mt-1 text-sm text-plat-muted">{todayStr}</p>
        </div>
      </div>

      {/* Trial expiry alert */}
      {counts.trialsEndingSoon > 0 && (
        <div className="flex items-center gap-3 rounded-xl border border-plat-warning-line bg-plat-warning-soft px-5 py-3.5">
          <Warning className="h-5 w-5 shrink-0 text-plat-warning-text" weight="fill" />
          <span className="text-sm font-medium text-plat-warning-text">
            {counts.trialsEndingSoon} organisation{counts.trialsEndingSoon > 1 ? 's' : ''} trial{counts.trialsEndingSoon > 1 ? 's' : ''} expire{counts.trialsEndingSoon === 1 ? 's' : ''} within 7 days.
          </span>
          <Link to="/platform/tenants?status=trial" className="ml-auto text-xs font-medium text-plat-warning-text underline hover:text-plat-warning-text">
            View tenants
          </Link>
        </div>
      )}

      {/* Stat cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <StatCard
          label="Total Tenants"
          value={counts.totalTenants}
          icon={<Buildings className="h-6 w-6" />}
          accent="text-plat-info-text"
        />
        <StatCard
          label="Active"
          value={counts.activeTenants}
          icon={<CheckCircle className="h-6 w-6" weight="fill" />}
          accent="text-plat-success-text"
        />
        <StatCard
          label="On Trial"
          value={counts.trialTenants}
          icon={<ClockCounterClockwise className="h-6 w-6" />}
          accent="text-plat-warning-text"
        />
        <StatCard
          label="Suspended"
          value={counts.suspendedTenants}
          icon={<XCircle className="h-6 w-6" weight="fill" />}
          accent="text-plat-danger-text"
        />
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard
          label="Trials Ending Soon"
          value={counts.trialsEndingSoon}
          icon={<Warning className="h-6 w-6" weight="fill" />}
          accent="text-plat-warning-text"
        />
        <StatCard
          label="Total Users"
          value={counts.totalUsers}
          icon={<Users className="h-6 w-6" />}
          accent="text-plat-info-text"
        />
        <StatCard
          label="Total Matters"
          value={counts.totalCases}
          icon={<Briefcase className="h-6 w-6" />}
          accent="text-plat-indigo-text"
        />
      </div>

      {/* Recent signups */}
      <div>
        <h3 className="mb-4 text-sm font-semibold text-plat-fg">Recent Signups</h3>
        {recentSignups.length === 0 ? (
          <div className="rounded-xl border border-plat-border bg-plat-surface px-6 py-8 text-center text-sm text-plat-muted">
            No recent signups.
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-plat-border bg-plat-surface">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-plat-border">
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Organisation</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Plan</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Status</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted">Created</th>
                  <th className="px-5 py-3.5 text-xs font-semibold uppercase tracking-[0.06em] text-plat-muted"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-plat-border">
                {recentSignups.map((org) => (
                  <tr key={org.id} className="transition hover:bg-plat-hover">
                    <td className="px-5 py-3.5">
                      <Link
                        to={`/platform/tenants/${org.id}`}
                        className="font-medium text-plat-fg hover:text-plat-info-text hover:underline"
                      >
                        {org.name}
                      </Link>
                      <div className="text-xs text-plat-muted">{org.slug}</div>
                    </td>
                    <td className="px-5 py-3.5"><PlanBadge plan={org.plan} /></td>
                    <td className="px-5 py-3.5"><StatusBadge status={org.status} /></td>
                    <td className="px-5 py-3.5 text-plat-secondary">{formatDate(org.createdAt)}</td>
                    <td className="px-5 py-3.5 text-right">
                      <Link
                        to={`/platform/tenants/${org.id}`}
                        className="text-xs font-medium text-plat-info-text hover:text-plat-info-text"
                      >
                        View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
